import { useQueryClient } from '@tanstack/react-query';
import React from 'react';
import { AppContext } from '../contexts/app-context';

/**
 * Report that a page (or a lazy section bundle) is loading, for the shell's
 * 3px bar under the App Bar. Counted, so overlapping reporters (a Suspense
 * fallback followed by the page's own loading state) never cut it short.
 */
export function useReportRouteLoading(params: { loading: boolean }) {
  // Context
  const { dispatch } = React.useContext(AppContext);

  React.useEffect(() => {
    if (!params.loading) {
      return;
    }
    dispatch({ type: 'ROUTE_LOADING_START' });
    return () => dispatch({ type: 'ROUTE_LOADING_END' });
  }, [dispatch, params.loading]);
}

/**
 * "Try again" for a page whose data didn't load. Refetches the mounted
 * react-query queries that failed, and remounts the page so the legacy
 * useEffect fetches run again too.
 */
export function useRetryRoute() {
  // Context
  const { dispatch } = React.useContext(AppContext);
  // Hooks
  const queryClient = useQueryClient();

  return React.useCallback(() => {
    queryClient.refetchQueries({
      type: 'active',
      predicate: (query) => query.state.status === 'error',
    });
    dispatch({ type: 'RETRY_ROUTE' });
  }, [dispatch, queryClient]);
}

/**
 * "Try again" for one section (a tab, drawer or panel): refetches the failed
 * react-query queries. When none failed (the section uses a legacy
 * useEffect fetch), it remounts the page so that fetch runs again.
 */
export function useRetrySection() {
  // Context
  const { dispatch } = React.useContext(AppContext);
  // Hooks
  const queryClient = useQueryClient();

  return React.useCallback(() => {
    const failed = queryClient
      .getQueryCache()
      .findAll({ type: 'active' })
      .filter((query) => query.state.status === 'error');
    if (failed.length) {
      queryClient.refetchQueries({
        type: 'active',
        predicate: (query) => query.state.status === 'error',
      });
    } else {
      dispatch({ type: 'RETRY_ROUTE' });
    }
  }, [dispatch, queryClient]);
}
